import React from 'react';
import { isMeasuredMetric } from '../GovernanceAnalyticsAiVisibility.utils';
import { BandedMetric, toneForMetric } from './bandVisuals.utils';
import { SentimentThumb } from './SentimentThumb';

/**
 * One metric cell in the Gaps table.
 *
 * Three renderings, because the three kinds of number answer different questions and a single bar
 * answers only one of them. Each variant REPLACES the others — none of them layer a new treatment
 * on top of the plain bar.
 *
 * UNMEASURED IS NOT ZERO, in every variant. A term with content but no AI answers has nothing to
 * measure, and coercing that to `0` drew a full-width empty bar reading "0%" — indistinguishable
 * from a term the AI genuinely never favoured. The two mean opposite things to a reader deciding
 * where to spend effort, so an unmeasured metric renders as an explicit dash.
 *
 * THE REFERENCE IS THE ROWS' OWN AVERAGE. It must come from the rows on screen; a line drawn from a
 * different population invites a comparison the reader cannot check. It is omitted entirely when
 * nothing was measured, because a marker at zero asserts an average that does not exist.
 */

/**
 * The narrowest fill drawn for a measured, non-zero value.
 *
 * A legibility floor for the fill only: the label always reads the true percentage, and a measured
 * zero draws nothing at all.
 */
const MINIMUM_VISIBLE_FILL_PERCENT = 1.5;

const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value * 100)));

/** How this metric should be drawn. */
export type GapsCellVariant =
  /** A share of a population, read against the full 0–100 axis. */
  | 'bar'
  /** A banded score: the band icon and the distance from the average, with no bar at all. */
  | 'band'
  /** A rate whose interesting range is far below 100%, drawn against the displayed maximum. */
  | 'relative';

/** The width a fill occupies, with the legibility floor applied to real non-zero values. */
function fillWidthPercent(share: number): number {
  if (share <= 0) {
    return 0;
  }
  return Math.max(MINIMUM_VISIBLE_FILL_PERCENT, Math.min(100, share));
}

/** "+4 pts" / "-3 pts" / undefined when there is nothing to measure against. */
function pointsDelta(percent: number, averagePercent?: number): string | undefined {
  if (averagePercent === undefined) {
    return undefined;
  }
  const delta = percent - averagePercent;
  if (delta === 0) {
    return 'level';
  }
  return `${delta > 0 ? '+' : ''}${delta} pts`;
}

export function GapsMetricCell({
  value,
  average,
  color,
  opacity,
  label,
  subject,
  bandMetric,
  variant = 'bar',
  displayedMaximum,
}: {
  /** The measured 0–1 value, or undefined when this row has nothing to measure. */
  value?: number;
  /** The displayed rows' mean for this metric, or undefined when none were measured. */
  average?: number;
  color: string;
  opacity: number;
  /** Metric name, for tooltips. */
  label: string;
  /**
   * Whose row this is.
   *
   * The cell renders one entity's number, so its tooltips name that entity rather than saying
   * "this term" — a tooltip vaguer than the row it sits in.
   */
  subject?: string;
  /** Set for banded metrics, which is what supplies the icon. */
  bandMetric?: BandedMetric;
  variant?: GapsCellVariant;
  /** For `relative`: the largest 0–1 value in the displayed set, which becomes full width. */
  displayedMaximum?: number;
}) {
  if (!isMeasuredMetric(value)) {
    return (
      <td className="p-3">
        <div
          className="relative h-6 rounded bg-slate-50 border border-dashed border-slate-200 d-flex align-items-center justify-content-center"
          title={`No answers mentioned ${subject ?? 'this tag'} in this window, so ${label} could not be measured.`}
        >
          <span className="text-[11px] font-semibold text-slate-400">—</span>
        </div>
      </td>
    );
  }

  const percent = clampPercent(value);
  const averagePercent = isMeasuredMetric(average) ? clampPercent(average) : undefined;
  const delta = pointsDelta(percent, averagePercent);
  const againstAverage =
    averagePercent === undefined
      ? 'No average for the rows shown'
      : `${delta === 'level' ? 'Level with' : `${delta} against`} the average of the rows shown (${averagePercent}%)`;

  // BANDED: the icon says where the score sits, the delta says how it compares. A bar alongside
  // them would be a third rendering of the same fact.
  if (variant === 'band' && bandMetric) {
    return (
      <td className="p-3">
        <div className="d-flex align-items-center gap-2" title={`${label}: ${percent}% · ${againstAverage}`}>
          <SentimentThumb tone={toneForMetric(bandMetric, value)} metric={bandMetric} title={label} />
          {delta === 'level' || delta === undefined ? (
            // Both are absence states, not measurements: level is "no distance" and the second is
            // "nothing to measure against". Full-strength text would read as data.
            <span className="text-[11px] tabular-nums text-slate-400">{delta ?? 'no average'}</span>
          ) : (
            <span className="text-[11px] font-semibold tabular-nums text-slate-600">{delta}</span>
          )}
        </div>
      </td>
    );
  }

  // RELATIVE: recommendation rates live in the low single digits, where a faithful 0–100 bar is a
  // sliver against a wide empty track and every row looks identically empty. Drawn against the
  // largest value on screen instead, so the column compares rows to each other — which is the only
  // comparison this number supports. No track behind it: the track is what implied the 0–100 axis
  // that does not apply here.
  if (variant === 'relative') {
    const maximumPercent = isMeasuredMetric(displayedMaximum) ? clampPercent(displayedMaximum) : undefined;
    const scale = (candidate: number) => (maximumPercent && maximumPercent > 0 ? (candidate / maximumPercent) * 100 : 0);

    return (
      <td className="p-3">
        {/* Same height as every other metric bar: a thinner bar in one column made the whole row
            read as ragged, and the height was never carrying meaning. No average marker — on a
            relative axis its position is a function of the largest row on screen, so it moved
            whenever the sort or the filter changed and measured nothing stable. */}
        <div
          className="relative h-6"
          title={`${label}: ${percent}% · ${againstAverage} · bar is relative to the highest on screen (${maximumPercent ?? 0}%)`}
        >
          <div className="h-full rounded-full" style={{ width: `${fillWidthPercent(scale(percent))}%`, backgroundColor: color, opacity }} />
          {/* Inside the bar, like the other columns — the true percentage, never the scaled one. */}
          <span
            className="absolute inset-0 d-flex align-items-center justify-content-center text-[11px] font-bold text-slate-700"
            style={{ textShadow: '1px 0 0 #fff, -1px 0 0 #fff, 0 1px 0 #fff, 0 -1px 0 #fff' }}
          >
            {percent}%
          </span>
        </div>
      </td>
    );
  }

  return (
    <td className="p-3">
      <div
        className="relative h-6 rounded bg-slate-100 border border-slate-200 overflow-hidden"
        title={`${label}: ${percent}% · ${againstAverage}`}
      >
        <div className="h-full" style={{ width: `${fillWidthPercent(percent)}%`, backgroundColor: color, opacity }} />
        {averagePercent !== undefined ? (
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-black"
            style={{ left: `calc(${averagePercent}% - 1px)` }}
            title={`Average of the rows shown: ${averagePercent}%`}
          />
        ) : null}
        <span
          className="absolute inset-0 d-flex align-items-center justify-content-center text-[11px] font-bold text-slate-700"
          style={{ textShadow: '1px 0 0 #fff, -1px 0 0 #fff, 0 1px 0 #fff, 0 -1px 0 #fff' }}
        >
          {percent}%
        </span>
      </div>
    </td>
  );
}
